/**
 * WideFormatDashboard - Dedicated dashboard for Wide Format jobs
 * 
 * Features:
 * - Shows only Wide Format and Wide Format Press jobs
 * - Displays material, printing, and finishing info
 * - Kanban-style board with stages
 * - Job detail modal with full specifications
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  RefreshCw, ArrowLeft, Printer, Package, Layers, 
  Clock, AlertTriangle, ChevronRight, X, Play
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Wide Format specific stages
const STAGES = [
  { id: 'prepress', title: 'PREPRESS', color: 'bg-blue-600', borderColor: 'border-blue-500' },
  { id: 'wide-format', title: 'WIDE FORMAT', color: 'bg-orange-600', borderColor: 'border-orange-500' },
  { id: 'finishing', title: 'FINISHING', color: 'bg-pink-600', borderColor: 'border-pink-500' },
  { id: 'packaging', title: 'PACKAGING', color: 'bg-amber-600', borderColor: 'border-amber-500' },
  { id: 'dispatch', title: 'DISPATCH', color: 'bg-teal-600', borderColor: 'border-teal-500' },
];

// Status colors
const STATUS_COLORS = {
  'Queued': 'bg-gray-500',
  'Ready': 'bg-blue-500',
  'In Production': 'bg-green-500',
  'Paused': 'bg-amber-500',
  'Blocked': 'bg-red-500',
  'Complete': 'bg-emerald-500',
};

const WideFormatDashboard = ({ onBack }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeJobIds, setActiveJobIds] = useState(new Set());
  const [lastSync, setLastSync] = useState(null);

  // Fetch wide format jobs
  const fetchJobs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/wide-format/jobs?limit=100`, {
        withCredentials: true
      });
      setJobs(response.data.jobs || []);
      setLastSync(new Date());
    } catch (error) {
      console.error('Failed to load wide format jobs:', error);
      toast.error('Failed to load wide format jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch active tracking
  const fetchActiveTracking = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/jobs/active-tracking`, {
        withCredentials: true
      });
      const activeIds = new Set((response.data.active_jobs || []).map(j => j.job_id));
      setActiveJobIds(activeIds);
    } catch (error) {
      console.error('Failed to fetch active tracking:', error);
    }
  }, []);

  // Sync from PrintLogic
  const syncFromPrintLogic = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/printlogic/sync-orders`, {}, {
        withCredentials: true
      });
      await fetchJobs();
      toast.success('Synced from PrintLogic');
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Initial load and auto-refresh
  useEffect(() => {
    fetchJobs();
    fetchActiveTracking();
    
    const jobsInterval = setInterval(fetchJobs, 60000); // Refresh every minute
    const trackingInterval = setInterval(fetchActiveTracking, 5000); // Check tracking every 5s
    
    return () => {
      clearInterval(jobsInterval);
      clearInterval(trackingInterval);
    };
  }, [fetchJobs, fetchActiveTracking]);

  // Group jobs by stage
  const getJobsByStage = (stageId) => {
    return jobs.filter(job => job.board_stage === stageId);
  };

  // Check if job is late
  const isJobLate = (job) => {
    if (!job.date_due || job.date_due === '0000-00-00') return false;
    const dueDate = new Date(job.date_due);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  // Job Card Component
  const JobCard = ({ job }) => {
    const late = isJobLate(job);
    const isActive = activeJobIds.has(job.job_id);
    const statusColor = STATUS_COLORS[job.board_status] || 'bg-gray-500';

    return (
      <div
        onClick={() => setSelectedJob(job)}
        className={`
          p-3 rounded-lg border cursor-pointer transition-all hover:shadow-lg
          ${isActive 
            ? 'bg-green-50 border-green-400 shadow-md' 
            : late 
              ? 'bg-red-50 border-red-300' 
              : 'bg-white border-gray-200 hover:border-gray-300'
          }
        `}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={`text-base font-bold ${isActive ? 'text-green-700' : late ? 'text-red-700' : 'text-blue-700'}`}>
                #{job.job_id}
              </span>
              {isActive && <Play className="w-4 h-4 text-green-600 animate-pulse" />}
              {late && !isActive && <AlertTriangle className="w-4 h-4 text-red-500" />}
            </div>
            <p className="text-sm font-medium text-gray-800 truncate">{job.customer}</p>
            <p className="text-xs text-gray-600 truncate">{job.product}</p>
          </div>
          <Badge className={`${statusColor} text-white text-xs`}>
            {job.board_status}
          </Badge>
        </div>

        {/* Material info - key feature for wide format */}
        {job.materials && (
          <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-200">
            <div className="flex items-center gap-1 text-orange-700 text-xs font-bold mb-1">
              <Layers className="w-3 h-3" />
              MATERIAL
            </div>
            <p className="text-xs text-orange-800">{job.materials}</p>
          </div>
        )}

        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
          <span>Qty: {job.quantity}</span>
          {job.date_due && job.date_due !== '0000-00-00' && (
            <span className={late ? 'text-red-600 font-bold' : ''}>
              Due: {new Date(job.date_due).toLocaleDateString('en-IE')}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Job Detail Modal
  const JobDetailModal = ({ job, onClose }) => {
    if (!job) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div 
          className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-orange-600 text-white p-4 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Job #{job.job_id}</h2>
                <p className="text-orange-100">{job.customer}</p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-orange-700 rounded-lg">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Product Info */}
            <div>
              <h3 className="text-sm font-bold text-gray-500 uppercase mb-2">Product</h3>
              <p className="text-lg text-gray-800">{job.product}</p>
              {job.item_title && job.item_title !== job.product && (
                <p className="text-sm text-gray-600 mt-1">{job.item_title}</p>
              )}
            </div>

            {/* Specifications Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                  <Package className="w-4 h-4" />
                  Quantity
                </div>
                <p className="text-2xl font-bold text-gray-800">{job.quantity}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                  <Clock className="w-4 h-4" />
                  Due Date
                </div>
                <p className={`text-2xl font-bold ${isJobLate(job) ? 'text-red-600' : 'text-gray-800'}`}>
                  {job.date_due && job.date_due !== '0000-00-00' 
                    ? new Date(job.date_due).toLocaleDateString('en-IE')
                    : 'Not set'
                  }
                </p>
              </div>
            </div>

            {/* Wide Format Specifications */}
            <div className="space-y-4">
              {/* Material */}
              {job.materials && (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2 text-orange-700 font-bold mb-2">
                    <Layers className="w-5 h-5" />
                    MATERIAL
                  </div>
                  <p className="text-orange-800 text-lg">{job.materials}</p>
                </div>
              )}

              {/* Printing */}
              {job.printing && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 text-blue-700 font-bold mb-2">
                    <Printer className="w-5 h-5" />
                    PRINTING
                  </div>
                  <p className="text-blue-800">{job.printing}</p>
                </div>
              )}

              {/* Finishing */}
              {job.finishing && (
                <div className="p-4 bg-pink-50 rounded-lg border border-pink-200">
                  <div className="flex items-center gap-2 text-pink-700 font-bold mb-2">
                    <ChevronRight className="w-5 h-5" />
                    FINISHING
                  </div>
                  <p className="text-pink-800 whitespace-pre-line">{job.finishing}</p>
                </div>
              )}
            </div>

            {/* Status */}
            <div className="flex items-center justify-between p-4 bg-gray-100 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">Current Stage</p>
                <p className="font-bold text-gray-800 uppercase">{job.board_stage}</p>
              </div>
              <Badge className={`${STATUS_COLORS[job.board_status] || 'bg-gray-500'} text-white`}>
                {job.board_status}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-orange-600 animate-spin mx-auto mb-4" />
          <p className="text-xl font-bold text-gray-800">Loading Wide Format Jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onBack} className="text-gray-600">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                <Printer className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-black text-gray-800">WIDE FORMAT</h1>
                <p className="text-sm text-gray-500">{jobs.length} active jobs</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {lastSync && (
              <span className="text-sm text-gray-500">
                Last sync: {lastSync.toLocaleTimeString()}
              </span>
            )}
            <Button 
              onClick={syncFromPrintLogic} 
              disabled={syncing}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync'}
            </Button>
          </div>
        </div>
      </header>

      {/* Kanban Board */}
      <div className="flex-1 p-4 overflow-x-auto">
        <div className="flex gap-4 min-w-max h-full">
          {STAGES.map((stage) => {
            const stageJobs = getJobsByStage(stage.id);
            
            return (
              <div key={stage.id} className="w-80 flex flex-col bg-white rounded-xl shadow-sm border border-gray-200">
                {/* Stage Header */}
                <div className={`${stage.color} px-4 py-3 rounded-t-xl`}>
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-black text-white uppercase tracking-wide">
                      {stage.title}
                    </h2>
                    <span className="bg-white/20 text-white text-sm font-bold px-2 py-1 rounded">
                      {stageJobs.length}
                    </span>
                  </div>
                </div>

                {/* Jobs List */}
                <div className="flex-1 p-3 space-y-3 overflow-y-auto max-h-[calc(100vh-200px)]">
                  {stageJobs.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No jobs</p>
                    </div>
                  ) : (
                    stageJobs.map((job) => (
                      <JobCard key={job.job_id} job={job} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Job Detail Modal */}
      {selectedJob && (
        <JobDetailModal job={selectedJob} onClose={() => setSelectedJob(null)} />
      )}
    </div>
  );
};

export default WideFormatDashboard;
